import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import useProgressStore from '../stores/useProgressStore';
import useAuthStore from '../stores/useAuthStore';
import DOMPurify from 'dompurify';
import DashboardLayout from '../components/DashboardLayout';

const LearningDashboardPage = () => {
    const { user } = useAuthStore();
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { course, isLoading: courseLoading, error: courseError } = useCourseStore();
    const { progress, isLoading: progressLoading } = useProgressStore();

    const fetchCourse = useCourseStore.getState().fetchCourse;

    const fetchProgress = useProgressStore.getState().fetchProgress;

    const [expandedModules, setExpandedModules] = useState({});

    useEffect(() => {
        fetchCourse(courseId);
        fetchProgress(courseId);
    }, [courseId, fetchProgress, fetchCourse]);

    useEffect(() => {
        if (course?.modules) {
            const initial = {};
            course.modules.forEach((_, i) => initial[i] = true);
            setExpandedModules(initial);
        }
    }, [course?.modules]);

    const getTotalLessons = () => {
        return course?.modules?.reduce((total, module) => total + (module.lessons?.length || 0), 0) || 0;
    };

    const getCompletedLessons = () => {
        return progress?.completedLessons?.length || 0;
    };

    const getModuleProgress = (moduleLessons) => {
        if (!progress?.completedLessons || !moduleLessons) return 0;
        const completed = moduleLessons.filter(lesson =>
            progress.completedLessons.includes(lesson._id.toString())
        ).length;
        return moduleLessons.length ? Math.round((completed / moduleLessons.length) * 100) : 0;
    };

    const handleLessonClick = (lessonId) => {
        navigate(`/student/learning/${courseId}/lesson/${lessonId}`);
    };

    const handleDiscussionClick = (courseId) => {
        navigate(`/student/learning/discussions/${courseId}`);
    };

    const handleCertificateClick = (courseId) => {
        navigate(`/student/course/${courseId}/certificate`);
    };

    const toggleModule = (moduleIndex) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleIndex]: !prev[moduleIndex]
        }));
    };

    if (courseLoading || progressLoading) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    if (courseError || !course) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Course not found</h2>
                        <button
                            onClick={() => navigate('/courses')}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                            Browse courses
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const totalLessons = getTotalLessons();
    const completedLessons = getCompletedLessons();
    const progressPercentage = totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0;

    return (
        <DashboardLayout userRole={user.role}>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <button
                            onClick={() => navigate(`/student/my-courses`)}
                            className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to my courses
                        </button>
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">{course.title}</h1>
                        <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course?.description) }} />
                        </div>
                    </div>

                    <div className='flex justify-end'>
                        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex justify-between items-center gap-4">

                            <button
                                onClick={() => handleDiscussionClick(course?._id)}
                                className="text-primary-600 hover:text-primary-800 dark:text-primary-400 text-sm font-medium"
                            >
                                + Discussions
                            </button>

                            <button
                                onClick={() => handleCertificateClick(course?._id)}
                                className="text-green-600 hover:text-green-800 dark:text-green-400 text-sm font-medium"
                            >
                                + Certificate
                            </button>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-8 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <div className="flex justify-between mb-2">
                            <span className="font-medium text-neutral-900 dark:text-white">
                                Course Progress
                            </span>
                            <span className="font-medium text-neutral-900 dark:text-white">
                                {completedLessons} / {totalLessons} lessons
                            </span>
                        </div>
                        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3">
                            <div
                                className="bg-primary-500 h-3 rounded-full"
                                style={{ width: `${progressPercentage}%` }}
                            ></div>
                        </div>
                        <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                            {progressPercentage}% complete
                        </div>
                    </div>

                    {/* Curriculum */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                                Course Curriculum
                            </h2>
                            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                                {totalLessons} lessons • {course.duration}
                            </p>
                        </div>
                        <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                            {course.modules?.map((module, moduleIndex) => {
                                const moduleLessons = module.lessons || [];
                                const moduleProgress = getModuleProgress(moduleLessons);
                                const isExpanded = expandedModules[moduleIndex] ?? true;

                                return (
                                    <div key={moduleIndex} className="p-6">
                                        <div
                                            className="flex items-center justify-between cursor-pointer"
                                            onClick={() => toggleModule(moduleIndex)}
                                        >
                                            <div className="flex items-center">
                                                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                                                    Module {moduleIndex + 1}: {module.title}
                                                </h3>
                                                <span className="ml-3 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                                                    {moduleProgress}%
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="text-sm text-neutral-500 dark:text-neutral-400 mr-3">
                                                    {moduleLessons.length} lessons
                                                </span>
                                                <span className="text-neutral-500 dark:text-neutral-400">
                                                    {isExpanded ? '▲' : '▼'}
                                                </span>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="mt-4 space-y-2">
                                                {moduleLessons.map((lesson) => {
                                                    const isCompleted = progress?.completedLessons?.includes(
                                                        lesson._id.toString()
                                                    );
                                                    const lessonNumber = moduleLessons.indexOf(lesson) + 1;

                                                    return (
                                                        <div
                                                            key={lesson._id}
                                                            className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                                                            onClick={() => handleLessonClick(lesson._id)}
                                                        >
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                                                                        {moduleIndex + 1}.{lessonNumber}
                                                                    </span>
                                                                </div>
                                                                <span className={`text-neutral-900 dark:text-white ${isCompleted ? 'line-through text-neutral-500 dark:text-neutral-400' : ''}`}>
                                                                    {lesson.title}
                                                                </span>
                                                            </div>
                                                            {isCompleted && (
                                                                <span className="text-success-600 dark:text-success-400 font-bold">✓</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default LearningDashboardPage;